import { useEffect, useState } from "react";
import { Printer, Download } from "lucide-react";
import api, { API, SITE_URL, qrTrackUrl } from "@/lib/api";
import { toast } from "sonner";

const slugify = (t) => (t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const SIZES = { small: { cls: "w-24 h-24", mm: 30 }, medium: { cls: "w-36 h-36", mm: 42 }, large: { cls: "w-48 h-48", mm: 55 } };

export default function AdminQRLabels() {
  const [products, setProducts] = useState([]);
  const [mode, setMode] = useState("product");
  const [slug, setSlug] = useState("");
  const [campaign, setCampaign] = useState({ name: "", url: SITE_URL });
  const [count, setCount] = useState(12);
  const [size, setSize] = useState("medium");
  const [caption, setCaption] = useState("Scan to view");
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products", { params: { admin: true } }).then(({ data }) => {
      setProducts(data);
      if (data[0]) { setSlug(data[0].slug); setPrice(data[0].price ? `$${Number(data[0].price).toFixed(2)}` : ""); }
    }).catch(() => {});
  }, []);

  const prod = products.find((p) => p.slug === slug);
  let dest = "", label = "", src = "";
  if (mode === "product" && prod) { dest = `${SITE_URL}/product/${prod.slug}`; label = prod.title; src = `label:${prod.slug}`; }
  else if (mode === "campaign") { label = campaign.name || "Campaign"; dest = campaign.url || SITE_URL; src = `campaign:${slugify(campaign.name) || "custom"}`; }
  const qrSrc = dest ? `${API}/qr?data=${encodeURIComponent(qrTrackUrl(dest, src))}&fill=1a1a1a&logo=1` : "";
  const cells = Array.from({ length: Math.max(1, Math.min(60, Number(count) || 1)) });
  const sz = SIZES[size];

  const captionBlock = (px) => `${caption ? `<p style="font-size:${px}px;letter-spacing:2px;text-transform:uppercase;color:#9a8f7d;margin:6px 0 0">${caption}</p>` : ""}${price ? `<p style="font-family:Georgia,serif;font-size:${px + 3}px;color:#1a1a1a;margin:3px 0 0">${price}</p>` : ""}`;

  const printSheet = () => {
    const cellsHtml = cells.map(() => `<div class="c"><img src="${qrSrc}"/><p class="n">${label}</p>${captionBlock(9)}</div>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>QR Labels — ${label}</title><style>body{font-family:Georgia,serif;margin:22px;color:#1a1a1a}.g{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}.c{text-align:center;page-break-inside:avoid;border:1px solid #e6ddce;padding:14px}.c img{width:${sz.mm * 3}px;height:${sz.mm * 3}px}.c .n{font-size:11px;letter-spacing:2px;text-transform:uppercase;margin:10px 0 0}</style></head><body><div class="g">${cellsHtml}</div></body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 600);
  };

  const toDataUrl = (url) => fetch(url).then((r) => r.blob()).then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }));

  const downloadPdf = async () => {
    if (!qrSrc) return;
    setBusy(true);
    try {
      const { jsPDF } = await import("jspdf");
      const img = await toDataUrl(qrSrc);
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const cols = 3, cellW = 63, cellH = 78, mx = 12, my = 14;
      let x = mx, y = my, c = 0;
      cells.forEach(() => {
        const cx = x + 31.5;
        doc.addImage(img, "PNG", x + (63 - sz.mm) / 2, y, sz.mm, sz.mm);
        let ty = y + sz.mm + 6;
        doc.setFont("times", "normal"); doc.setFontSize(9); doc.text(label.substring(0, 30), cx, ty, { align: "center" });
        if (caption) { ty += 5; doc.setFontSize(7); doc.setTextColor(154, 143, 125); doc.text(caption.toUpperCase(), cx, ty, { align: "center" }); doc.setTextColor(26, 26, 26); }
        if (price) { ty += 5; doc.setFontSize(10); doc.text(price, cx, ty, { align: "center" }); }
        c++; x += cellW;
        if (c % cols === 0) { x = mx; y += cellH; }
        if (y > 250 && c < cells.length) { doc.addPage(); x = mx; y = my; }
      });
      doc.save(`qr-labels-${slugify(label) || "sheet"}.pdf`);
      toast.success("PDF downloaded.");
    } catch (e) { toast.error("Could not generate PDF."); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-qr-labels">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading text-3xl">QR Label Generator</h1><p className="font-body text-sm text-ink/50 mt-1">Print or export branded, trackable QR sheets for hang-tags, shelf cards & market stalls.</p></div>
        <div className="flex gap-2 no-print">
          <button onClick={downloadPdf} className="adm-btn-outline" disabled={!qrSrc || busy} data-testid="qr-pdf"><Download size={14} /> {busy ? "Building…" : "Download PDF"}</button>
          <button onClick={printSheet} className="adm-btn" disabled={!qrSrc} data-testid="qr-print"><Printer size={14} /> Print Sheet</button>
        </div>
      </div>

      <div className="adm-card space-y-4 mb-8 no-print">
        <div className="flex gap-1">
          {[["product", "Product"], ["campaign", "Custom Campaign"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`qrlabel-mode-${k}`} className={`font-body uppercase tracking-wide2 text-[0.6rem] px-4 py-2 border transition-colors ${mode === k ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`}>{l}</button>
          ))}
        </div>
        {mode === "product" ? (
          <div><label className="adm-label">Product</label>
            <select className="adm-input" value={slug} onChange={(e) => { setSlug(e.target.value); const p = products.find((x) => x.slug === e.target.value); setPrice(p?.price ? `$${Number(p.price).toFixed(2)}` : ""); }} data-testid="qrlabel-product">
              {products.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
            </select>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="adm-label">Campaign / Label Name</label><input className="adm-input" value={campaign.name} onChange={(e) => setCampaign((c) => ({ ...c, name: e.target.value }))} placeholder="e.g. Summer Market Stall" data-testid="qrlabel-name" /></div>
            <div><label className="adm-label">Destination URL</label><input className="adm-input" value={campaign.url} onChange={(e) => setCampaign((c) => ({ ...c, url: e.target.value }))} data-testid="qrlabel-url" /></div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="adm-label">Caption line</label><input className="adm-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Scan to view" data-testid="qrlabel-caption" /></div>
          <div><label className="adm-label">Price line (optional)</label><input className="adm-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$248.00" data-testid="qrlabel-price" /></div>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <label className="adm-label">Size</label>
            <div className="flex gap-1">{Object.keys(SIZES).map((s) => <button key={s} onClick={() => setSize(s)} data-testid={`qrlabel-size-${s}`} className={`font-body uppercase tracking-wide2 text-[0.6rem] px-3 py-1.5 border transition-colors ${size === s ? "bg-ink text-linen border-ink" : "border-ink/20 text-ink/60 hover:border-ink"}`}>{s}</button>)}</div>
          </div>
          <div className="flex-1 min-w-[200px]"><label className="adm-label">Labels per sheet ({cells.length})</label><input type="range" min="1" max="30" value={count} onChange={(e) => setCount(e.target.value)} className="w-full accent-ink" data-testid="qrlabel-count" /></div>
        </div>
        <p className="font-body text-[0.65rem] text-ink/40">Tracking source: <span className="font-mono">{src}</span> — every scan appears in Analytics → QR Scans.</p>
      </div>

      <div id="qr-sheet">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cells.map((_, i) => (
            <div key={i} className="border hairline p-3 text-center bg-cream flex flex-col items-center" data-testid={i === 0 ? "qrlabel-preview" : undefined}>
              {qrSrc && <img src={qrSrc} alt={label} className={sz.cls} />}
              <p className="font-body text-[0.6rem] uppercase tracking-wide2 mt-2 text-ink/70">{label}</p>
              {caption && <p className="font-body text-[0.55rem] uppercase tracking-wide2 text-ink/40 mt-1">{caption}</p>}
              {price && <p className="font-heading text-sm mt-0.5">{price}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
